import { trpc } from '@/main'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { useSessionUser } from '@/hooks/use-session'
import * as v from 'valibot'
import { Test } from '@/routes/_app/-components/applicants/test'
import { TestStepPending } from '@/routes/_app/-components/shared/flow-pending'
import { flowViewTransition } from '@/lib/utils'

export const Route = createFileRoute('/_app/test/personality')({
  validateSearch: v.object({
    retake: v.optional(v.boolean(), false),
    resume: v.optional(v.boolean()),
    // Carried through onboarding so the final landing can return to the visitor's intended destination.
    redirect: v.optional(v.string()),
  }),
  loaderDeps: ({ search }) => ({ retake: search.retake }),
  context: ({ deps }) => ({
    // No cache-lifetime overrides — the `gcTime: 0` that used to sit here was inert AND broke the
    // prefetch once it moved onto context. See test.competences.tsx.
    testQuestionsQueryOptions: trpc.applicants.getQuestions.queryOptions({
      type: 'personality',
      newTest: deps.retake,
    }),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.testQuestionsQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: TestStepPending,
})

function RouteComponent() {
  const navigate = useNavigate()
  const context = Route.useRouteContext()
  const { data: questionsResponse } = useSuspenseQuery(context.testQuestionsQueryOptions)
  const user = useSessionUser()
  const { retake, redirect } = Route.useSearch()
  // Logged-out → /login is enforced centrally by the `_app` layout guard (see route-access).
  // Self-registered applicants continue to the profile wizard (which honors `redirect` on completion);
  // company-invited employees skip it (their profile comes from the company) and go straight to their
  // report. Only the self-registered path carries `redirect` — employees arrive via invitation, never
  // the compare CTA.
  const isEmployee = user?.type === 'applicant' && user.isEmployee
  return (
    <Test
      questionsResponse={questionsResponse}
      type="personality"
      nextStep={() =>
        isEmployee
          ? navigate({ to: '/report', viewTransition: flowViewTransition() })
          : navigate({ to: '/register/applicant', search: { redirect }, viewTransition: flowViewTransition() })
      }
      previousStep={() => navigate({ to: '/test/values', search: { redirect }, viewTransition: flowViewTransition() })}
      newTest={retake}
    />
  )
}
